import type { Editor } from '@tiptap/core';
import type { DropdownLayoutOption } from '@wix/design-system';
import { DropdownBase, listItemSectionBuilder } from '@wix/design-system';
import Fuse from 'fuse.js';
import React, {
  forwardRef,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import { EditorContext, PluginsContext, RicosContext, ZIndexContext } from './../../../ricos-context';
import type { ModalService } from './../../../ricos-modals';
import { useModalService } from './../../../ricos-modals/modal-service-provider';
import type { TFunction } from './../../../ricos-types/commonTypes';
import type { PublicCommands } from './../../../ricos-types/editorCommandsType';
import type { QuickAction } from './../../../ricos-types/pluginTypes';
import { quickActionsModalSections } from './../../../ricos-types/pluginTypes';
import type { LogBiFn } from './../../../_shared/bi-context-provider/bi-context-provider';
import { useBiNext } from './../../../_shared/bi-context-provider/bi-context-provider';
import { UiComponentsProvider } from './../../../ui-components';

import { QUICK_ACTIONS_ROOT } from '../data-hooks';
import type { QuickActionsModalProps, QuickActionsModalRef } from '../types';
import { QUICK_ACTIONS_MODAL_ID } from '../types';
import styles from './modal-scss';

const COMMONLY_USED: string[] = ['writeWithAi', 'image', 'gallery', 'button', 'youtube-video'];

export const QuickActionsModal = forwardRef<QuickActionsModalRef, QuickActionsModalProps>(
  (props, ref) => {
    const { query, openedThroughModalService } = props;

    const { adapter, getPublicCommands } = useContext(EditorContext);
    const plugins = useContext(PluginsContext);
    const { t } = useContext(RicosContext);
    const zIndexService = useContext(ZIndexContext);
    const actions = useMemo(() => plugins.getQuickActions(), []);
    const [refElement, setRefElement] = useState<HTMLDivElement | null>(null);
    const logBi = useBiNext({ modalId: QUICK_ACTIONS_MODAL_ID });

    const modalService = useModalService();
    const publicCommands = getPublicCommands();

    const options = useMemo(() => {
      const filtered = filterActions(actions, query || '', t);

      const actionsInSectionsMap = filtered.reduce((groups, action) => {
        if (!query && COMMONLY_USED.includes(action.id)) {
          groups[quickActionsModalSections.CommonlyUsed] ??= [];
          groups[quickActionsModalSections.CommonlyUsed].push(action);
        }

        groups[action.section] ??= [];

        return {
          ...groups,
          [action.section]: [...groups[action.section], action],
        };
      }, {} as { [key: string]: QuickAction[] });

      const quickActionOptionProps = {
        publicCommands,
        props,
        modalService,
        t,
        openedThroughModalService,
        editor: adapter.tiptapEditor,
        logBi,
      };

      const organizedOptions = !query
        ? Object.values(quickActionsModalSections).reduce<DropdownLayoutOption[]>(
            (list, section) => {
              return [
                ...list,

                ...(actionsInSectionsMap[section]?.length > 0
                  ? createSection({
                      items: actionsInSectionsMap[section],
                      section,
                      ...quickActionOptionProps,
                    })
                  : []),
              ];
            },
            []
          )
        : Object.entries(actionsInSectionsMap).reduce<DropdownLayoutOption[]>(
            (list, [section, items]) => {
              return [
                ...list,
                ...(items.length > 0
                  ? createSection({
                      items,
                      section,
                      ...quickActionOptionProps,
                    })
                  : []),
              ];
            },
            []
          );

      if (organizedOptions[organizedOptions.length - 1]?.id === '-') {
        organizedOptions.pop();
      }
      return organizedOptions;
    }, [query]);

    const [selectedId, setSelectedId] = useState(
      options.find(option => option.value === 'action')?.id
    );

    const [open, setOpen] = useState(false);
    const currElement: React.MutableRefObject<null | DropdownBase> = useRef(null);

    useEffect(() => {
      setSelectedId(options.find(option => option.value === 'action')?.id);
      setOpen(!!options.length);
    }, [query]);

    useImperativeHandle(ref, () => ({
      onKeyDown: ({ event }) => {
        if (['ArrowUp', 'ArrowDown'].includes(event.key)) {
          const direction = event.key === 'ArrowUp' ? -1 : 1;
          const activeOptions = options.filter(option => option.value === 'action');
          if (!activeOptions.length) {
            return true;
          }
          setSelectedId(prev => {
            const currIndex = activeOptions.findIndex(({ id }) => id === prev);
            const nextIndex = (currIndex + direction + activeOptions.length) % activeOptions.length;
            return activeOptions[nextIndex].id;
          });
          return true;
        }

        if (event.key === 'Enter') {
          const option = options.find(option => option.id === selectedId);
          if (option && 'onClick' in option) {
            option?.onClick?.();
            return true;
          }
          return false;
        }

        return false;
      },
    }));

    return (
      <div
        className={styles.quickActionsModal}
        style={{ zIndex: zIndexService.getZIndex('DIALOG') }}
        ref={setRefElement}
      >
        {refElement && (
          <UiComponentsProvider containerEl={refElement}>
            <DropdownBase
              ref={currElement}
              open={open}
              placement="bottom-start"
              minWidth={280}
              maxHeight={250}
              className={styles.quickActionsModal}
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus={openedThroughModalService}
              focusOnOption={selectedId}
              dataHook={QUICK_ACTIONS_ROOT}
              options={options}
              flip={false}
              listType="action"
            />
          </UiComponentsProvider>
        )}
      </div>
    );
  }
);

type QuickActionOptionProps = {
  section: string;
  publicCommands: PublicCommands;
  props: QuickActionsModalProps;
  modalService: ModalService;
  t: TFunction;
  logBi: LogBiFn;
  openedThroughModalService?: boolean;
  editor: Editor;
};

type Option = {
  as: string;
  dataHook: string;
  ellipsis: boolean;
  id: string;
  onClick: () => void;
  optionTitle: string;
  prefixIcon?: JSX.Element;
  shouldFocusWithoutScroll: boolean;
  size: string;
  title: boolean;
  value: string;
};

const commandToQuickActionOption = ({
  quickAction,
  publicCommands,
  props,
  editor,
  modalService,
  t,
  section,
  openedThroughModalService,
  logBi,
}: QuickActionOptionProps & {
  quickAction: QuickAction;
}): Option => {
  return {
    as: 'button',
    dataHook:
      section === quickActionsModalSections.CommonlyUsed
        ? `${quickAction.title}${section}`
        : quickAction.title,
    ellipsis: false,
    id: `${quickAction.id}${section}`,
    onClick: () => {
      props?.command?.(props);
      quickAction.command({ publicCommands, editor, modalService });

      logBi({ dataHook: QUICK_ACTIONS_ROOT, value: quickAction.id });
      if (openedThroughModalService) {
        modalService.closeModal(QUICK_ACTIONS_MODAL_ID);
      }
    },
    optionTitle: t(quickAction.title),
    prefixIcon: quickAction.icon ? <quickAction.icon /> : undefined,
    shouldFocusWithoutScroll: true,
    size: 'small',
    title: false,
    value: 'action',
  };
};

const createSection = ({
  items,
  section,
  t,
  ...rest
}: QuickActionOptionProps & {
  items: QuickAction[];
}) => {
  return [
    listItemSectionBuilder({
      title: t(section),
      id: section,
    }),

    ...items.map((item: QuickAction) =>
      commandToQuickActionOption({
        quickAction: item,
        section,
        t,
        ...rest,
      })
    ),

    listItemSectionBuilder({
      type: 'divider',
      id: '-',
    }),
  ];
};

const filterActions = (actions: QuickAction[], query: string, t: TFunction) => {
  if (!query) {
    return actions;
  }

  if (query.match(/^ /)?.length || (query.match(/ /gm)?.length || 0) > 1) {
    return [];
  }

  const queryIsSectionName = Object.values(quickActionsModalSections).find(
    val => t(val).toLowerCase() === query.toLowerCase()
  );
  if (queryIsSectionName) {
    return actions.filter(({ section }) => t(section).toLowerCase() === query.toLowerCase());
  }

  const fuse = new Fuse(actions, {
    keys: [
      { name: 'title', getFn: action => t(action.title), weight: 0.7 },
      { name: 'section', getFn: action => t(action.section), weight: 0.3 },
      { name: 'tags', getFn: action => action.tags.join(' '), weight: 0.2 },
    ],
    threshold: 0.4,
    minMatchCharLength: query.length > 1 ? 2 : 1,
  });

  const result = fuse.search(query);

  if (result.length) {
    return result.map(({ item }) => item);
  }

  return actions.filter(item => t(item.title).toLowerCase().includes(query.toLowerCase()));
};
